import React, { useState, useEffect } from 'react';
import eventBus, { EVENT_NAMES } from '../../utils/event_bus';
import {
  Datagrid,
  TextField,
  FunctionField,
  useList,
  ListContextProvider,
  useDataProvider
} from 'react-admin';
import { Driver } from '../../models/Driver';
import { TruckLength, TruckType } from '../../utils/truck-utils';
import { 
  Box, 
  Card, 
  CardContent, 
  Chip, 
  Stack, 
  TextField as MuiTextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import zhCN from 'date-fns/locale/zh-CN';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { QueryLocationDriversParams } from '../../models/request/QueryLocationDriversParams';

// 定义通用的过滤器类型
type FilterType = {
  name: string;
  phone: string;
  distance: number; // 搜索距离（米）
  pos_from?: number; // 最后位置时间的开始时间戳
  pos_to?: number; // 最后位置时间的结束时间戳
};

// 车型选项
const truckTypeOptions = [
  { value: '', label: '全部' },
  { value: '0', label: '平板' },
  { value: '1', label: '高栏' },
  { value: '2', label: '厢式' },
  { value: '3', label: '自卸' },
  { value: '4', label: '冷藏' },
  { value: '5', label: '爬梯' },
  { value: '6', label: '集装箱' },
  { value: '7', label: '高低板' },
];

// 查询表单组件
const SearchForm: React.FC<{
  filters: FilterType;
  setFilters: React.Dispatch<React.SetStateAction<FilterType>>;
  onSearch: () => void;
  onReset: () => void;
  mapLocation: { latitude: number; longitude: number; address: string; } | null;
  onDistanceChange: (distance: number) => void;
}> = ({ filters, setFilters, onSearch, onReset, mapLocation, onDistanceChange }) => {

  // 日期选择器通用样式
  const datePickerSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
      '&.Mui-focused fieldset': { borderColor: 'white' },
    },
    '& .MuiInputBase-input': {
      paddingTop: '6px',
      paddingBottom: '6px',
    },
    '& .MuiSvgIcon-root': { color: 'white' },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhCN}>
      <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
        <Grid container spacing={2}>
          {/* 姓名和手机号搜索条件已移除，接口暂不支持 */}


          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <DatePicker
                  label="开始时间"
                  value={filters.pos_from ? new Date(filters.pos_from) : null}
                  onChange={(date) => {
                    setFilters({
                      ...filters,
                      pos_from: date ? date.getTime() : undefined
                    });
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      InputProps: { style: { color: 'white' } },
                      InputLabelProps: { style: { color: 'rgba(255, 255, 255, 0.7)' } },
                    }
                  }}
                  sx={datePickerSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <DatePicker
                  label="结束时间"
                  value={filters.pos_to ? new Date(filters.pos_to) : null}
                  onChange={(date) => {
                    setFilters({
                      ...filters,
                      pos_to: date ? date.getTime() : undefined
                    });
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      InputProps: { style: { color: 'white' } },
                      InputLabelProps: { style: { color: 'rgba(255, 255, 255, 0.7)' } },
                    }
                  }}
                  sx={datePickerSx}
                />
              </Box>
            </Box>
          </Grid>
          
          {/* 距离选择和查询重置按钮 */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
              <FormControl 
                size="small"
                sx={{ 
                  width: '200px',
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'white' },
                  },
                  '& .MuiInputBase-input': {
                    paddingTop: '6px',
                    paddingBottom: '6px',
                  },
                  '& .MuiSvgIcon-root': { color: 'white' }
                }}
              >
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>搜索距离</InputLabel>
                <Select
                  value={filters.distance}
                  label="搜索距离"
                  onChange={(e) => {
                    const newDistance = Number(e.target.value);
                    setFilters({ ...filters, distance: newDistance });
                    // 发送距离变更事件
                    eventBus.publish(EVENT_NAMES.DISTANCE_CHANGE, newDistance / 1000);
                    
                    // 调用父组件提供的距离变更回调
                    onDistanceChange(newDistance);
                  }}
                >
                  <MenuItem value={5000}>5公里</MenuItem>
                  <MenuItem value={10000}>10公里</MenuItem>
                  <MenuItem value={20000}>20公里</MenuItem>
                  <MenuItem value={50000}>50公里</MenuItem>
                  <MenuItem value={100000}>100公里</MenuItem>
                  <MenuItem value={150000}>150公里</MenuItem>
                  <MenuItem value={200000}>200公里</MenuItem>
                  <MenuItem value={250000}>250公里</MenuItem>
                  <MenuItem value={300000}>300公里</MenuItem>
                  <MenuItem value={350000}>350公里</MenuItem>
                  <MenuItem value={400000}>400公里</MenuItem>
                  <MenuItem value={450000}>450公里</MenuItem>
                  <MenuItem value={500000}>500公里</MenuItem>
                </Select>
              </FormControl>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={onReset}
                  startIcon={<ClearIcon />}
                  sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                >
                  重置
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={onSearch}
                  startIcon={<SearchIcon />}
                >
                  查询
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

const AllDriverListView: React.FC = () => {
  // 不再需要分页状态
  
  // 提示消息状态
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // 获取 dataProvider
  const dataProvider = useDataProvider();
  
  // 计算默认的开始时间（3个月前）
  const defaultPosFrom = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date.getTime();
  };
  
  const [filters, setFilters] = useState<FilterType>({
    name: '',
    phone: '',
    distance: 50000, // 默认50公里
    pos_from: undefined, // 不设置默认开始时间
    pos_to: undefined // 不设置默认结束时间
  });
  
  const [searchParams, setSearchParams] = useState<FilterType>({
    name: '',
    phone: '',
    distance: 50000, // 默认50公里
    pos_from: undefined,
    pos_to: undefined
  });
  
  // 地图位置状态
  const [mapLocation, setMapLocation] = useState<{
    latitude: number;
    longitude: number;
    address: string;
  } | null>(null);
  
  // 司机数据状态
  const [driverData, setDriverData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // 处理位置变化
  const handleLocationChange = (latitude: number, longitude: number, address: string) => {
    console.log('位置变化:', latitude, longitude, address);
    setMapLocation({ latitude, longitude, address });
    
    // 使用新位置信息获取司机列表，并传递当前的过滤器值
    // 这样可以确保使用用户设置的距离参数
    fetchDriversByLocation(latitude, longitude, filters);
  };
  
  // 订阅地图位置变化事件
  useEffect(() => {
    // 订阅地图位置变化事件
    const unsubscribe = eventBus.subscribe(
      EVENT_NAMES.MAP_LOCATION_CHANGE,
      handleLocationChange
    );
    
    // 组件卸载时取消订阅
    return () => unsubscribe();
  }, []);
  
  // 首次加载时，延迟发送默认距离的DISTANCE_CHANGE事件
  useEffect(() => {
    const timer = setTimeout(() => {
      // 发送默认距离变更事件
      eventBus.publish(EVENT_NAMES.DISTANCE_CHANGE, filters.distance);
    }, 500); // 延迟500毫秒
    
    return () => clearTimeout(timer);
  }, []);
  
  // 使用 dataProvider.driversByLocation 获取司机列表
  const fetchDriversByLocation = async (lat?: number, lon?: number, customFilters?: FilterType) => {
    setIsLoading(true);
    
    try {
      // 计算默认的时间范围（如果用户没有选择）
      const defaultFrom = () => {
        const date = new Date();
        date.setMonth(date.getMonth() - 3); // 默认3个月前
        return date.getTime();
      };
      
      // 使用传入的自定义过滤器或当前过滤器
      const currentFilters = customFilters || filters;
      
      // 构建查询参数
      const params: QueryLocationDriversParams = {
        lat: lat || (mapLocation?.latitude || 0),
        lon: lon || (mapLocation?.longitude || 0),
        distance: currentFilters.distance, // 使用当前过滤器中的距离值
        pos_from: searchParams.pos_from ?? defaultFrom(), // 如果用户没有选择，使用默认值
        pos_to: searchParams.pos_to ?? new Date().getTime() // 如果用户没有选择，使用当前时间
      };
      
      // 调用 dataProvider.driversByLocation 接口
      const result = await dataProvider.driversByLocation(params);
      
      // 直接使用接口返回的数据，不再进行姓名和手机号过滤
      const filteredData = result.data;
      
      // 更新数据状态
      setDriverData(filteredData);
      setTotal(filteredData.length);
      
      // 发布司机数据更新事件
      eventBus.publish(EVENT_NAMES.DRIVER_DATA_UPDATED, filteredData);
    } catch (error) {
      console.error('获取司机列表失败:', error);
      setSnackbarMessage('获取司机列表失败');
      setSnackbarOpen(true);
      setDriverData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 处理查询
  const handleSearch = () => {
    // 更新搜索参数
    setSearchParams({ ...filters });
    
    // 如果有地图位置信息，则使用当前位置获取司机列表
    if (mapLocation) {
      // 直接传递当前的 filters 值，确保使用最新的距离设置
      fetchDriversByLocation(mapLocation.latitude, mapLocation.longitude, filters);
    } else {
      // 否则提示用户需要选择位置
      setSnackbarMessage('请在地图上选择位置');
      setSnackbarOpen(true);
    }
  };
  
  // 处理重置
  const handleReset = () => {
    const defaultFilters: FilterType = {
      name: '',
      phone: '',
      distance: 50000, // 默认50公里
      pos_from: undefined, // 重置时清除日期选择
      pos_to: undefined // 重置时清除日期选择
    };
    setFilters(defaultFilters);
    setSearchParams(defaultFilters);
    
    // 发送默认距离变更事件
    eventBus.publish(EVENT_NAMES.DISTANCE_CHANGE, defaultFilters.distance);
    
    // 如果有地图位置信息，则使用当前位置获取司机列表
    if (mapLocation) {
      fetchDriversByLocation(mapLocation.latitude, mapLocation.longitude, defaultFilters);
    }
  };
  
  // 创建列表上下文，不使用分页
  const listContext = useList({ data: driverData, isLoading });
  
  // 处理 Snackbar 关闭
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* 显示当前地图位置信息 */}
      {mapLocation && (
        <Box sx={{ mb: 1, p: 1, backgroundColor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1, whiteSpace: 'nowrap' }}>
              当前位置:
            </Typography>
            <Chip 
              size="small" 
              label={`${mapLocation.address || '未知地址'} (${mapLocation.latitude.toFixed(4)}, ${mapLocation.longitude.toFixed(4)})`}
              color="primary"
              variant="outlined"
              sx={{ maxWidth: '100%', overflow: 'hidden' }}
            />
          </Box>
          <IconButton 
            size="small" 
            onClick={() => {
              setMapLocation(null);
              setDriverData([]);
              setTotal(0);
            }}
            sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      
      {/* 添加提示消息 */}
       <Snackbar 
         open={snackbarOpen} 
         autoHideDuration={3000} 
         onClose={handleSnackbarClose}
         anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
         sx={{ marginTop: '50px' }}
       >
        <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* 查询表单 */}
      <SearchForm 
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearch}
        onReset={handleReset}
        mapLocation={mapLocation}
        onDistanceChange={(newDistance) => {
          // 如果有地图位置信息，则使用当前位置和新的距离值获取司机列表
          if (mapLocation) {
            // 创建一个包含新距离值的临时过滤器对象
            const tempFilters = { ...filters, distance: newDistance };
            fetchDriversByLocation(mapLocation.latitude, mapLocation.longitude, tempFilters);
          }
        }}
      />
      <Box sx={{ flex: 1, width: '100%' }}>
        <ListContextProvider value={{ ...listContext, total: total || 0 }}>
          {/* 显示总记录数 */}
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              共 {total} 条记录
            </Typography>
          </Box>
          <Datagrid 
            rowClick={(id, resource, record) => {
              // 发布司机选中事件，将选中的司机信息发送给地图组件
              console.log('司机被选中:', record);
              // 发送司机信息到地图，使地图主动移动到车辆位置并显示司机信息标注
              eventBus.publish(EVENT_NAMES.DRIVER_SELECTED, record);
              // 返回 false 阻止默认导航行为，防止触发页面重新加载
              return false;
            }} 
            bulkActionButtons={false}
            size="small"
            sx={{
              width: '100%',
              '& .RaDatagrid-headerCell': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem',
              },
              '& .RaDatagrid-row': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              },
              '& .MuiTableCell-root': {
                color: 'white',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '6px 16px',
                fontSize: '0.875rem',
              },
              '& .MuiTable-root': {
                width: '100%',
                tableLayout: 'fixed',
              },
            }}
          >
            <TextField source="name" label="姓名" />
            <TextField source="phone" label="手机号" />
            <FunctionField
              label="更新时间"
              render={(record: any) => {
                return record.updatedAt ? new Date(record.updatedAt).toLocaleString() : '未知';
              }}
            />
          </Datagrid>
        </ListContextProvider>
      </Box>
    </Box>
  );
};

export default AllDriverListView;